import * as uamMethods from '../../consts/uam/uamMethods';
import UamClient from './uamClient';
import {log} from '../log';
import {setUamAsset} from '../../state/slices/uamData';
import {
    setAutoCleanInProgress,
    setFindMyDeviceStatus,
    setResetDeviceInProgress,
    setUpdateSettingsInProgress,
    updateDeviceData,
} from '../../state/slices/iotData';
import * as uamActionStatusTypes from '../../consts/uam/uamActionStatusTypes';
import {selectUamAssetId} from '../../state/selectors/uamData';
import {
    selectIsAutoCleanInProgress,
    selectIsFindMyDeviceInProgress,
    selectIsResetDeviceInProgress,
    selectIsUpdateSettingsInProgress,
} from '../../state/selectors/iotData';
import uamFlowService from './uamFlowService';
import uamTimeouts from '../../consts/uam/uamTimeouts';
import * as uamFlowTypes from '../../consts/uam/uamFlowTypes';
import errorService from '../errors/errorService';
import {MW_ERRORS} from '../../consts/app/mwErrors';
import * as cmFindDeviceActionTypes from '../../consts/communicationLayer/cmFindDeviceActionTypes';
import findMyDeviceStatuses from '../../consts/device/findMyDeviceStatuses';
import {subscribeToSocketsAndWaitConnected} from '../iot/iotConnectionService';
import iotService from '../iot/iotService';

const getAsset = async (isHolder) => {
    const response = await new UamClient().callGet({
        methodName: uamMethods.GET_ASSETS,
        isHolder,
    });

    const [deviceAsset] = response || [];
    const assetId = deviceAsset?.assetId;

    if (!assetId) {
        log.error(`uamClientService: failed to get asset. isHolder: ${isHolder}`);

        return false;
    }

    setUamAsset(assetId, isHolder);

    return true;
};

//use definedAssetId to retrieve data for NOT connected device
const getUamInfo = async (isHolder, uamTokenData) => {
    const {assetId: definedAssetId} = uamTokenData || {};
    const assetId = definedAssetId || selectUamAssetId(isHolder);

    if (!assetId) throw errorService.createError(MW_ERRORS.ERROR_IOT_NO_ASSET_ID, 'AssetId is not provided');

    const deviceInfos = await new UamClient().callGet({
        methodName: uamMethods.GET_DEVICE_INFO,
        requestConfig: {args: assetId},
        isHolder,
        uamTokenData,
    });

    if (!deviceInfos?.length) return false;

    const deviceInfo = deviceInfos[0];

    if (!deviceInfo) return false;

    //don't update state if not a connected device have been changed
    if (!definedAssetId) {
        updateDeviceData(isHolder, deviceInfo);
    }
    //synchronizeDataRequestCurrentTries = 0;
    return deviceInfo;
};

const connectDevice = (assetId, isHolder) =>
    new UamClient().callGet({
        methodName: uamMethods.CONNECT_DEVICE,
        requestConfig: {args: assetId},
        isHolder,
    });

const assetSubscribe = (assetId, isHolder) =>
    new UamClient().callGet({
        methodName: uamMethods.ASSET_SUBSCRIBE,
        requestConfig: {args: assetId},
        isHolder,
    });

const checkUamRequestStatus = async (requestId, isHolder) => {
    try {
        if (!requestId) return;

        const assetId = selectUamAssetId(isHolder);

        if (!assetId) return;

        const data = await new UamClient().callGet({
            methodName: uamMethods.CHECK_REQUEST_STATUS,
            requestConfig: {args: [assetId, requestId]},
            isHolder,
        });

        const isFailed = data?.status === uamActionStatusTypes.FAILED;
        const isSuccess = data?.status === uamActionStatusTypes.SUCCESS;
        const isCanceled = data?.status === uamActionStatusTypes.CANCELED;

        if (isSuccess || isFailed || isCanceled) {
            return data;
        }
    } catch (error) {
        //ignore for now
    }

    return {status: uamActionStatusTypes.PENDING};
};

const getLogMessageForUpdateSettingsData = (data) => {
    if (!data) return '';

    const {settingName, newValue} = data;

    if (!settingName) return '';

    return `SettingName: ${settingName}, new value: ${JSON.stringify(newValue)}`;
};

const updateUamSettings = async (data, isHolder) => {
    const onError = (code, message) => {
        const error = errorService.createError(code, message);

        setUpdateSettingsInProgress(isHolder, false, error);
        return false;
    };

    if (!data) return onError(MW_ERRORS.ERROR_WRONG_DATA, 'Provided data is wrong');

    const isUpdateSettingsInProgress = selectIsUpdateSettingsInProgress(isHolder);

    if (isUpdateSettingsInProgress) return onError(MW_ERRORS.ERROR_IOT_FLOW_ALREADY_IN_PROGRESS, 'Already in progress');

    const logBody = getLogMessageForUpdateSettingsData(data);
    const assetId = selectUamAssetId(isHolder);

    if (!assetId) return onError(MW_ERRORS.ERROR_IOT_NO_ASSET_ID, 'AssetId is not provided');

    try {
        await subscribeToSocketsAndWaitConnected(isHolder);

        setUpdateSettingsInProgress(isHolder, true);
        log.info(`Start updateSettings process. isHolder: ${!!isHolder}. ${logBody}`);

        const {requestId} =
            (await new UamClient().callPost({
                methodName: uamMethods.UPDATE_SETTINGS,
                requestConfig: {args: assetId, data},
                isHolder,
            })) || {};

        if (!requestId) return onError(MW_ERRORS.ERROR_IOT_NO_REQUEST_ID, 'Request id is not defined');

        uamFlowService.createFlow({
            type: uamFlowTypes.UPDATE_SETTINGS,
            timeoutInSec: uamTimeouts.UAM_UPDATE_SETTING_TIMEOUT,
            isHolder,
            requestId,
        });

        return true;
    } catch (e) {
        const customError = iotService.getCustomIotError(e);

        setUpdateSettingsInProgress(isHolder, false, customError);
    }
};

const findMyDevice = async (data) => {
    const onError = (code, message) => {
        const error = errorService.createError(code, message);

        setFindMyDeviceStatus(findMyDeviceStatuses.ERROR, error);
        return false;
    };

    if (!data) return onError(MW_ERRORS.ERROR_WRONG_DATA, 'Provided data is wrong');

    const isStop = data.action === cmFindDeviceActionTypes.STOP;

    const isFindMyDeviceInProgress = selectIsFindMyDeviceInProgress();

    if (isFindMyDeviceInProgress) return onError(MW_ERRORS.ERROR_IOT_FLOW_ALREADY_IN_PROGRESS, 'Already in progress');

    try {
        await subscribeToSocketsAndWaitConnected();

        setFindMyDeviceStatus(isStop ? findMyDeviceStatuses.STOP : findMyDeviceStatuses.START);
        log.info(`Start findMyDevice process. Command: ${isStop ? 'Stop' : 'Start'}`);

        const assetId = selectUamAssetId();

        if (!assetId) return onError(MW_ERRORS.ERROR_IOT_NO_ASSET_ID, 'AssetId is not provided');

        const {requestId} =
            (await new UamClient().callPost({
                methodName: uamMethods.FIND_MY_DEVICE,
                requestConfig: {args: assetId, params: data},
            })) || {};

        if (!requestId) return onError(MW_ERRORS.ERROR_IOT_NO_REQUEST_ID, 'Request id is not defined');

        uamFlowService.createFlow({
            type: uamFlowTypes.FIND_MY_DEVICE,
            timeoutInSec: uamTimeouts.UAM_UPDATE_SETTING_TIMEOUT,
            requestId,
        });

        return true;
    } catch (e) {
        const customError = iotService.getCustomIotError(e);

        setFindMyDeviceStatus(findMyDeviceStatuses.ERROR, customError);
    }
};

const resetMyDevice = async (isHolder) => {
    const onError = (code, message) => {
        const error = errorService.createError(code, message);

        setResetDeviceInProgress(isHolder, false, error);
        return false;
    };

    const isResetDeviceInProgress = selectIsResetDeviceInProgress(isHolder);

    if (isResetDeviceInProgress) return onError(MW_ERRORS.ERROR_IOT_FLOW_ALREADY_IN_PROGRESS, 'Already in progress');

    const assetId = selectUamAssetId(isHolder);

    if (!assetId) return onError(MW_ERRORS.ERROR_IOT_NO_ASSET_ID, 'AssetId is not provided');

    try {
        await subscribeToSocketsAndWaitConnected(isHolder);

        setResetDeviceInProgress(isHolder, true);
        log.info(`Start resetDevice process. isHolder: ${isHolder}.`);

        const {requestId} =
            (await new UamClient().callPost({
                methodName: uamMethods.RESET_DEVICE,
                requestConfig: {args: assetId},
                isHolder,
            })) || {};

        if (!requestId) return onError(MW_ERRORS.ERROR_IOT_NO_REQUEST_ID, 'Request id is not defined');

        uamFlowService.createFlow({
            type: uamFlowTypes.RESET_DEVICE,
            timeoutInSec: uamTimeouts.UAM_UPDATE_SETTING_TIMEOUT,
            isHolder,
            requestId,
        });

        return true;
    } catch (e) {
        const customError = iotService.getCustomIotError(e);

        setResetDeviceInProgress(isHolder, false, customError);
    }
};

const autoClean = async () => {
    const onError = (code, message) => {
        const error = errorService.createError(code, message);

        setAutoCleanInProgress(false, error);
        return false;
    };

    const isAutoCleanInProgress = selectIsAutoCleanInProgress();

    if (isAutoCleanInProgress) return onError(MW_ERRORS.ERROR_IOT_FLOW_ALREADY_IN_PROGRESS, 'Already in progress');

    try {
        await subscribeToSocketsAndWaitConnected();

        setAutoCleanInProgress(true);
        log.info(`Start autoClean process.`);

        const assetId = selectUamAssetId();

        if (!assetId) return onError(MW_ERRORS.ERROR_IOT_NO_ASSET_ID, 'AssetId is not provided');

        const {requestId} =
            (await new UamClient().callPost({
                methodName: uamMethods.AUTO_CLEAN,
                requestConfig: {args: assetId},
            })) || {};

        if (!requestId) return onError(MW_ERRORS.ERROR_IOT_NO_REQUEST_ID, 'Request id is not defined');

        uamFlowService.createFlow({
            type: uamFlowTypes.AUTO_CLEAN,
            timeoutInSec: uamTimeouts.UAM_AUTO_CLEAN_TIMEOUT,
            requestId,
        });

        return true;
    } catch (e) {
        const customError = iotService.getCustomIotError(e);

        setAutoCleanInProgress(false, customError);
    }
};

/*

const getUamDevicesExperienceData = async ({assetId, data, headerData, codentify}) => {
    if (!data || !assetId) {
        return;
    }

    const {args, params} = data || {};
    const requestConfig = {
        args: [assetId, ...args],
        params,
    };

    if (headerData) {
        const {authToken, apiKey} = headerData || {};

        requestConfig.headers = {id_token: authToken, 'X-API-Key': apiKey};
    }

    try {
        return await new UamClient().callGet({
            methodName: uamMethods.GET_DEVICES_EXPERIENCE_DATA,
            requestConfig,
            codentify,
            isHolder
        });
    } catch (e) {
        log.error(`getUamDevicesExperienceData  error${e}`);
    }
};

const SYNCHRONIZE_DATA_RETRY_COUNT = 3;
let synchronizeDataRequestCurrentTries = 0;
const proceedSynchronizeAllData = async (isHolder, isRetry = false, dataCollectionType) => {
    if (isRetry) {
        synchronizeDataRequestCurrentTries++;
    }

    const isGlobalRetryExceeded = synchronizeDataRequestCurrentTries >= SYNCHRONIZE_DATA_RETRY_COUNT;
    const isDiagnosticRetryExceeded =
        synchronizeDataRequestCurrentTries >= 1 &&
        dataCollectionType === dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC;

    if (isGlobalRetryExceeded || isDiagnosticRetryExceeded) {
        // set device data collection to success to run holder data collection flow or stop holder data collection
        dataCollectionService.setDataCollectionFlowReceived({isReceived: true, isHolder, dataCollectionType});

        log.error(`SynchronizeAllData failed. Retry exceeded. isHolder: ${isHolder}`);
        return resetSynchronizeDataCollection(dataCollectionType, isHolder);
    }

    const state = getState();
    const isDataCollectionReceived = dataCollectionService.getIsDataCollectionReceived(dataCollectionType, isHolder);

    if (isDataCollectionReceived) return resetSynchronizeDataCollection(dataCollectionType, isHolder);

    const isYapSynchronizeDataCollectionInProgress = makeSelectIsYapSynchronizeDataCollectionInProgress()(state);
    const isYapUsageFlowDataCollectionInProgress = makeSelectIsYapUsageFlowDataCollectionInProgress()(state);
    const isDiagnosticFlowDataCollectionInProgress = selectIsDiagnosticFlowDataCollectionInProgress(state);

    if (
        (isYapSynchronizeDataCollectionInProgress ||
            isYapUsageFlowDataCollectionInProgress ||
            isDiagnosticFlowDataCollectionInProgress) &&
        !isRetry
    )
        return;

    const isHolderConnected = selectIsHolderConnected(state);

    if (!isHolderConnected && isHolder) return resetSynchronizeDataCollection(dataCollectionType, isHolder);

    await synchronizeAllData(isHolder, dataCollectionType);
};

const getDataCollectionUserAgent = () => {
    const appId = appConfig.getIccApiHeader_a();
    const appVersion = appConfig.getAppVersion();
    const userAgent = userBrowserService.getUserAgent();

    return `${appId}/${appVersion} ${userAgent}`;
};

const resetSynchronizeDataCollection = (dataCollectionType, isHolder) => {
    synchronizeDataRequestCurrentTries = 0;

    if (dataCollectionType === dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC) {
        advanceDiagnosticService.setDiagnosticFailed(isHolder);
        return;
    }

    dataCollectionService.setDataCollectionFlowInProgress(false, dataCollectionType);
};

const synchronizeAllData = async (isHolder, dataCollectionType) => {
    const state = getState();
    const assetId = selectUamAssetId(isHolder);

    dataCollectionService.setDataCollectionFlowInProgress(true, dataCollectionType, isHolder);

    await yapConnectionService.syncAndSubscribeOnDevice(isHolder);
    const isConnected = await cmService.waitUntilConnected({isHolder});

    if (!isConnected) return resetSynchronizeDataCollection(dataCollectionType, isHolder);

    //use getState to check last actual state
    const isYapDataCollectionAllowed = makeSelectIsYapDataCollectionAllowed()(getState());

    if (!isYapDataCollectionAllowed) return resetSynchronizeDataCollection(dataCollectionType, isHolder);

    log.info(`SynchronizeAllData is started for ${dataCollectionType} isHolder: ${isHolder}`);

    if (dataCollectionType === dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC) {
        amplitudeADTracking.trackADAutoDiagnosticStarted(isHolder);
    }

    const body = {overrideEntitiesToCollect: dataCollectionService.getEntitiesToCollect(dataCollectionType)};

    const onFail = async () => {
        await helpers.timeout(1000);
        return await proceedSynchronizeAllData(isHolder, true, dataCollectionType);
    };

    await new UamClient()
        .callPost({
            methodName: uamMethods.SYNCHRONIZE_ALL_DATA,
            requestConfig: {args: assetId, data: body},
            isHolder
        })
        .then((response) => {
            const {requestId} = response || {};

            if (requestId) {
                dispatch(setDataCollectionLastRequestId(requestId, isHolder));

                advanceDiagnosticService.setCurrentCodentify(dataCollectionType, isHolder);

                uamFlowService.createFlow({
                    type: uamFlowTypes.DATA_COLLECTION,
                    timeoutInSec: uamTimeouts.UAM_DATA_COLLECTION_TIMEOUT,
                    isHolder,
                    data: dataCollectionType,
                });
                return;
            }

            onFail();
        });
};

const CANCEL_RETRY_COUNT = 3;
let cancelCurrentTries = 0;

const cancelSynchronizeData = async (props) => {
    const {isRetry, dataCollectionType} = props || {};
    const state = getState();
    const isYapCancelDataCollectionInProgress = makeSelectYapCancelDataCollectionInProgress()(state);

    if (isYapCancelDataCollectionInProgress && !isRetry) return;
    dispatch(setYapCancelDataCollectionInProgress(true));

    const isDataCollectionInProgress = dataCollectionService.isDataCollectionInProgress(dataCollectionType);

    if (!isDataCollectionInProgress) {
        dispatch(setYapCancelDataCollectionInProgress(false));
        return;
    }

    log.info(`cancelSynchronizeData is started for ${dataCollectionType} isRetry: ${isRetry}`);

    const afterCancel = (isResetProgress = false) => {
        const isDataCollectionInProgress = dataCollectionService.isDataCollectionInProgress(dataCollectionType);

        if (isDataCollectionInProgress && isResetProgress) {
            dataCollectionService.setDataCollectionFlowInProgress(false, dataCollectionType);
            dispatch(setDataCollectionLastRequestId(null, isHolder));
        }

        dispatch(setYapCancelDataCollectionInProgress(false));
        cancelCurrentTries = 0;
    };

    const onFail = async (error, isResetProgress = false) => {
        if (cancelCurrentTries < CANCEL_RETRY_COUNT) {
            cancelCurrentTries++;
            await helpers.timeout(1000);
            await cancelSynchronizeData({isRetry: true, dataCollectionType});
        } else {
            log.info(`cancelSynchronizeData failed. error: ${error}`);
            afterCancel(isResetProgress);
        }
    };

    const deviceRequestId = makeSelectDataCollectionLastRequestId()(state);
    const holderRequestId = makeSelectDataCollectionLastRequestId(true)(state);
    const isHolder = !!holderRequestId;
    const previousRequestId = isHolder ? holderRequestId : deviceRequestId;

    if (!previousRequestId) {
        await onFail('Request id is not defined to cancel data collection', true);
        return;
    }

    const assetId = selectUamAssetId(isHolder);

    if (!assetId) {
        dispatch(setYapCancelDataCollectionInProgress(false));
        return;
    }

    try {
        const response = await new UamClient().callGet({
            methodName: uamMethods.CANCEL_REQUEST,
            requestConfig: {args: [assetId, previousRequestId]},
            isHolder
        });

        const {code} = response || {};
        const CANCEL_SUCCESS_CODE = 'REQUEST_CANCELED';
        const REQUEST_ALREADY_FINISHED = 'REQUEST_ALREADY_FINISHED';
        const REQUEST_CANCELATION_SCHEDULED = 'REQUEST_CANCELATION_SCHEDULED';

        if (code === CANCEL_SUCCESS_CODE || code === REQUEST_ALREADY_FINISHED) {
            if (dataCollectionType === dataCollectionTypes.DATA_COLLECTION_GLOBAL) {
                amplitudeDataCollectionTracking.trackGlobalDataCollectionError(isHolder);
            }
            afterCancel(true);
            return true;
        } else if (code === REQUEST_CANCELATION_SCHEDULED) {
            cancelCurrentTries = 0;
            return true;
        }
        await onFail('Could not cancel synchronize all data');
    } catch (e) {
        await onFail(e);
    }
};

const getAssetsByToken = async ({uamAuthToken, uamApiKey}) => {
    if (!uamAuthToken || !uamApiKey) return null;

    return await new UamClient().callGet({
        methodName: uamMethods.GET_ASSETS,
        requestConfig: {
            headers: {id_token: uamAuthToken, 'X-API-Key': uamApiKey},
        },
        isHolder
    });
};

const getInferenceInfo = async (assetId, inferenceName) => {
    if (!inferenceName) return null;

    return await new UamClient().callGet({
        methodName: uamMethods.GET_INFERENCE_INFO,
        requestConfig: {
            args: [assetId, inferenceName],
        },
        isHolder
    });
};
*/
export default {
    assetSubscribe,
    connectDevice,
    getAsset,
    getUamInfo,
    checkUamRequestStatus,
    updateUamSettings,
    resetMyDevice,
    findMyDevice,
    autoClean,
    /*
    cancelSynchronizeData,
    proceedSynchronizeAllData,
    getUamDevicesExperienceData,
    getAssetsByToken,
    getInferenceInfo,*/
};
